import * as React from 'react';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import cn from 'classnames';

import { AirportFragment, TemperatureUnits, WeatherFragment, WeatherTypes } from '@websky/graphql';
import { CdnIcon, CdnIconNames } from '@shared/ui';

import { useTheme } from '../../theme';
import { getTemperaturePrefix } from '../utils';

export const weatherIcons: { [type: string]: CdnIconNames } = {
	[WeatherTypes.Clear]: 'Weather/Clear',
	[WeatherTypes.Clouds]: 'Weather/Сlouds',
	BrokenClouds: 'Weather/Сlouds',
	[WeatherTypes.Drizzle]: 'Weather/Drizzle',
	[WeatherTypes.Thunderstorm]: 'Weather/Thunderstorm',
	[WeatherTypes.Rain]: 'Weather/Rain',
	[WeatherTypes.Snow]: 'Weather/Snow'
};

export interface Props {
	airport: AirportFragment;
	weatherInfo: WeatherFragment;
	className?: string;
}

const Weather: React.FC<Props> = React.memo(
	({ airport, weatherInfo, className }) => {
		const { Weather: theme } = useTheme('Weather');

		const cityImage = airport?.city?.images?.panorama?.url;
		const weatherIcon = weatherInfo?.type && weatherIcons[weatherInfo.type];
		const hasImage = !!cityImage;

		return (
			<Card classes={{ root: cn(theme.card, className, { [theme.card_withoutPanorama]: !hasImage }) }}>
				<CardActionArea classes={{ focusHighlight: theme.card__focusHighlight }}>
					{hasImage && (
						<CardMedia classes={{ root: theme.card__panorama }} image={cityImage} title={airport.name} />
					)}

					<div className={theme.card__content}>
						{weatherInfo && (
							<Card classes={{ root: theme.card__weather }}>
								{weatherIcon && <CdnIcon className={theme.card__weatherIcon} icon={weatherIcon} />}

								<div className={theme.card__temperature}>
									{getTemperaturePrefix(weatherInfo.temperature)}
									{Math.abs(Math.round(weatherInfo.temperature))}&deg;
									{weatherInfo.measurement === TemperatureUnits.Fahrenheit && 'F'}
								</div>
							</Card>
						)}
						<div className={cn(theme.card__airportName, { [theme.card__airportName_withIATA]: !hasImage })}>
							{!hasImage && <div className={theme.card__airportIATA}>{airport.iata}</div>}

							<span className={theme.card__airportLink}>
								{airport.name}

								<CdnIcon className={theme.card__chevronIcon} icon="Arrow/Chevron_right" />
							</span>
						</div>
					</div>
				</CardActionArea>
			</Card>
		);
	},
	(oldProps, newProps) => {
		return oldProps.weatherInfo && newProps.weatherInfo && oldProps.weatherInfo.type === newProps.weatherInfo.type;
	}
);

export default Weather;
